import { useState } from "react";
import {
  SkyBody1,
  SkyButton,
  SkyPopover,
  SkyInput,
  SkyBadge,
  SkyDivider,
} from "@skypointcloud/skypoint-ui";
import { Pin, X, Search } from "lucide-react";

import { getInstanceIdFromUrl } from "../../../utils/instanceId";
import { useNotification } from "../../../common/hooks/useNotification";
import { useGetProspectsQuery } from "../../../store/api/crmPersonsApi";
import {
  useGetSessionPinsQuery,
  usePinSessionToPersonMutation,
  useUnpinSessionMutation,
  type SessionPin,
} from "../../../store/api/crmAgentPinsDraftsApi";

/**
 * Attach this conversation to a prospect's record, from the chat.
 *
 * A counselor works one family for weeks and every conversation about them is
 * scattered down a chat list. This puts the thread on the record — as a LINK. The
 * conversation is not copied anywhere; the prospect's record shows a pointer back
 * to this same thread, so it keeps up as the conversation continues.
 *
 * ## The prospect picker reuses the real prospect search
 *
 * `useGetProspectsQuery` is the same endpoint the Prospects page uses, so it is
 * community-scoped server-side and shows exactly the people this counselor may
 * work. A hand-rolled search here would be a second, weaker one that can disagree
 * with the real list about who exists — the same trap the skyBrain page recorded
 * when it declined to build its own person picker.
 *
 * ## `personVisible` is not decoration
 *
 * A pin is readable by whoever can see the THREAD; that is not the same set as
 * whoever can see the PROSPECT. When they differ the server returns the pin with
 * its name withheld, and this component renders it as an attachment WITHOUT a
 * name rather than dropping the row. Dropping it would invite the reader to pin
 * the same family again; printing a blank chip would look like a bug. Saying "a
 * prospect you cannot see" is the honest third option.
 */

/** One attached prospect, named only if this reader is allowed to know. */
const PinnedRow = ({
  pin,
  onRemove,
  busy,
}: {
  pin: SessionPin;
  onRemove: (pin: SessionPin) => void;
  busy: boolean;
}) => (
  <li
    data-testid="thread-pin"
    data-visible={pin.personVisible ? "yes" : "no"}
    className="flex items-center justify-between gap-2 py-1"
  >
    <SkyBody1 className="truncate text-sm">
      {pin.personVisible ? (
        pin.personName
      ) : (
        // Never a blank, never a raw id. The attachment is real and the reader is
        // told it exists; the identity is the part they are not entitled to.
        <span className="italic text-muted-foreground">A prospect you cannot see</span>
      )}
    </SkyBody1>
    <SkyButton
      appearance="subtle"
      size="small"
      icon={X}
      disabled={busy}
      aria-label={
        pin.personVisible
          ? `Detach this chat from ${pin.personName}`
          : "Detach this chat from that prospect"
      }
      title="Detach"
      onClick={() => onRemove(pin)}
    />
  </li>
);

const ThreadPinControl = ({ sessionId }: { sessionId: string | null }) => {
  const instanceId = getInstanceIdFromUrl() || "";
  const { addNotification } = useNotification();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data } = useGetSessionPinsQuery(
    { instanceId, sessionId: sessionId ?? "" },
    // A thread that has not been created yet cannot be pinned to anything.
    { skip: !instanceId || !sessionId },
  );

  const [pinToPerson, { isLoading: isPinning }] = usePinSessionToPersonMutation();
  const [unpin, { isLoading: isUnpinning }] = useUnpinSessionMutation();

  const pins = data?.pins ?? [];
  const capabilityOff = data?.enabled === false;

  // Only search once there is something to search for. The prospects endpoint is
  // paged and unfiltered it would return the whole list into a dropdown.
  const trimmed = search.trim();
  const { data: results, isFetching } = useGetProspectsQuery(
    // `tab: "all"` — the widest set this counselor is entitled to, so the picker
    // can reach any prospect they could pin to. It is still ceiling-scoped on the
    // server; "all" means all of THEIRS, not all of the customer's.
    { instanceId, tab: "all", search: trimmed, page: 1, limit: 8 },
    { skip: !open || trimmed.length < 2 },
  );

  const notifyError = (error: unknown, fallback: string) => {
    addNotification({
      type: "ERROR",
      message: (error as { data?: { error?: string } })?.data?.error ?? fallback,
    });
  };

  const attach = async (personId: string, label: string) => {
    try {
      await pinToPerson({ instanceId, sessionId: sessionId as string, personId }).unwrap();
      addNotification({ type: "SUCCESS", message: `This chat is now on ${label}'s record.` });
      setSearch("");
      setOpen(false);
    } catch (error) {
      notifyError(error, "Could not attach this chat.");
    }
  };

  const remove = async (pin: SessionPin) => {
    try {
      await unpin({ instanceId, pinId: pin.id }).unwrap();
      addNotification({ type: "SUCCESS", message: "Detached." });
    } catch (error) {
      notifyError(error, "Could not detach that.");
    }
  };

  // Absence is the gate, and a thread with no id yet has nothing to attach.
  if (capabilityOff || !sessionId) return null;

  const alreadyPinned = new Set(pins.map((p) => p.personId));

  return (
    <SkyPopover
      open={open}
      onOpenChange={setOpen}
      placement="top-start"
      testId="thread-pin-control"
      trigger={
        <SkyButton
          appearance={pins.length ? "secondary" : "transparent"}
          size="small"
          aria-label={
            pins.length
              ? `This chat is on ${pins.length} ${pins.length === 1 ? "record" : "records"}`
              : "Attach this chat to a prospect"
          }
          title="Attach this chat to a prospect"
          className={`px-1.5 ${pins.length ? "" : "text-muted-foreground"}`}
          onClick={() => setOpen((o) => !o)}
        >
          <Pin
            className="h-4 w-4"
            aria-hidden="true"
          />
          {pins.length > 0 && (
            <SkyBadge
              appearance="tint"
              color="brand"
              size="small"
            >
              {pins.length}
            </SkyBadge>
          )}
        </SkyButton>
      }
    >
      <div
        className="w-80 p-2"
        data-testid="thread-pin-panel"
      >
        <SkyBody1 className="block px-1 font-semibold">Attach this chat to a prospect</SkyBody1>
        <SkyBody1 className="mt-1 block px-1 text-sm">
          It appears on their record as a link to this conversation, and keeps up as the chat
          continues. Nothing is copied.
        </SkyBody1>

        {pins.length > 0 && (
          <>
            <SkyDivider className="my-2" />
            <SkyBody1 className="block px-1 text-sm font-medium">Already on</SkyBody1>
            <ul className="px-1">
              {pins.map((pin) => (
                <PinnedRow
                  key={pin.id}
                  pin={pin}
                  onRemove={remove}
                  busy={isUnpinning}
                />
              ))}
            </ul>
          </>
        )}

        <SkyDivider className="my-2" />

        <div className="px-1">
          <SkyInput
            label="Find a prospect"
            placeholder="Type a name"
            value={search}
            contentBefore={
              <Search
                className="h-3.5 w-3.5"
                aria-hidden="true"
              />
            }
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div
          className="mt-2 max-h-56 overflow-y-auto px-1"
          data-testid="thread-pin-results"
        >
          {trimmed.length < 2 ? (
            <SkyBody1 className="block py-2 text-sm text-muted-foreground">
              Type at least two letters.
            </SkyBody1>
          ) : isFetching ? (
            <SkyBody1 className="block py-2 text-sm">Searching…</SkyBody1>
          ) : (results?.data ?? []).length === 0 ? (
            <SkyBody1 className="block py-2 text-sm">
              No prospects match, among the ones you can see.
            </SkyBody1>
          ) : (
            <ul className="flex flex-col">
              {(results?.data ?? []).map((row) => {
                const label =
                  [row.first_name, row.last_name].filter(Boolean).join(" ") || "Unnamed prospect";
                const done = alreadyPinned.has(row.id);
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      data-testid="thread-pin-candidate"
                      disabled={done || isPinning}
                      className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted disabled:opacity-50"
                      onClick={() => attach(row.id, label)}
                    >
                      <span className="truncate">{label}</span>
                      {/* The community is shown because two families can share a
                          name and the counselor needs to pick the right one. */}
                      {row.community_name && (
                        <span className="shrink-0 text-sm text-muted-foreground">
                          {row.community_name}
                        </span>
                      )}
                      {done && <span className="shrink-0 text-sm">Already on</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </SkyPopover>
  );
};

export default ThreadPinControl;
